package dev.jfronny.zerointerest.db

import androidx.room3.ColumnTypeConverter
import androidx.room3.ColumnTypeConverters
import androidx.room3.ConstructedBy
import androidx.room3.Dao
import androidx.room3.Database
import androidx.room3.Entity
import androidx.room3.Insert
import androidx.room3.OnConflictStrategy
import androidx.room3.Query
import androidx.room3.RoomDatabase
import androidx.room3.RoomDatabaseConstructor
import de.connect2x.trixnity.core.model.UserId
import dev.jfronny.zerointerest.data.TrustState
import kotlinx.coroutines.flow.Flow
import kotlinx.serialization.json.Json

@Database(
    entities = [
        SummaryTrustEntity::class,
        SummaryHeadEntity::class,
        SummaryEntity::class,
        SummaryTransactionEntity::class,
        TransactionTemplateEntity::class,
    ],
    version = 4,
)
@ConstructedBy(ZeroInterestRoomDatabaseConstructor::class)
@ColumnTypeConverters(ZeroInterestTypeConverters::class)
abstract class ZeroInterestRoomDatabase : RoomDatabase() {
    abstract fun summaryTrustDao(): SummaryTrustDao
    abstract fun summaryHeadDao(): SummaryHeadDao
    abstract fun summaryDao(): SummaryDao
    abstract fun transactionTemplateDao(): TransactionTemplateDao
}

// This is needed for Room which generates the actuals for us
@Suppress("KotlinNoActualForExpect", "EXPECT_ACTUAL_CLASSIFIERS_ARE_IN_BETA_WARNING")
expect object ZeroInterestRoomDatabaseConstructor : RoomDatabaseConstructor<ZeroInterestRoomDatabase> {
    override fun initialize(): ZeroInterestRoomDatabase
}

@Entity(primaryKeys = ["roomId", "id"])
data class TransactionTemplateEntity(
    val roomId: String,
    val id: String,
    val description: String,
    val sender: String,
    val receivers: Map<UserId, Long>,
)

@Dao
interface TransactionTemplateDao {
    @Query("SELECT * FROM TransactionTemplateEntity WHERE roomId = :roomId")
    fun getTemplates(roomId: String): Flow<List<TransactionTemplateEntity>>

    @Insert(onConflict = OnConflictStrategy.REPLACE)
    suspend fun insert(entity: TransactionTemplateEntity)

    @Query("DELETE FROM TransactionTemplateEntity WHERE roomId = :roomId AND id = :id")
    suspend fun delete(roomId: String, id: String)
}

@Entity(primaryKeys = ["roomId", "summaryId", "parentId"])
data class SummaryEntity(
    val roomId: String,
    val summaryId: String,
    val parentId: String,
)

@Entity(primaryKeys = ["roomId", "summaryId", "transactionId"])
data class SummaryTransactionEntity(
    val roomId: String,
    val summaryId: String,
    val transactionId: String,
)

@Dao
interface SummaryDao {
    @Insert(onConflict = OnConflictStrategy.REPLACE)
    suspend fun insertSummary(entities: List<SummaryEntity>)

    @Insert(onConflict = OnConflictStrategy.REPLACE)
    suspend fun insertTransactions(entities: List<SummaryTransactionEntity>)

    @Query("SELECT parentId FROM SummaryEntity WHERE roomId = :roomId AND summaryId = :summaryId")
    suspend fun getParents(roomId: String, summaryId: String): List<String>

    @Query("SELECT transactionId FROM SummaryTransactionEntity WHERE roomId = :roomId AND summaryId IN (:summaryIds)")
    suspend fun getTransactions(roomId: String, summaryIds: List<String>): List<String>

    @Query("SELECT * FROM SummaryTransactionEntity WHERE roomId = :roomId AND transactionId IN (:transactionIds)")
    suspend fun getSummariesForTransactions(roomId: String, transactionIds: List<String>): List<SummaryTransactionEntity>

    @Query("DELETE FROM SummaryEntity WHERE roomId = :roomId")
    suspend fun clearSummaries(roomId: String)

    @Query("DELETE FROM SummaryTransactionEntity WHERE roomId = :roomId")
    suspend fun clearSummaryTransactions(roomId: String)
}

@Entity(primaryKeys = ["roomId", "eventId"])
data class SummaryTrustEntity(
    val roomId: String,
    val eventId: String,
    val state: TrustState,
)

@Dao
interface SummaryTrustDao {
    @Insert(onConflict = OnConflictStrategy.REPLACE)
    suspend fun insert(entity: SummaryTrustEntity)

    @Query("SELECT state FROM SummaryTrustEntity WHERE roomId = :roomId AND eventId = :eventId")
    suspend fun getTrustState(roomId: String, eventId: String): TrustState?

    @Query("DELETE FROM SummaryTrustEntity WHERE roomId = :roomId")
    suspend fun clear(roomId: String)
}

class ZeroInterestTypeConverters {
    @ColumnTypeConverter
    fun toTrustState(value: String) = enumValueOf<TrustState>(value)

    @ColumnTypeConverter
    fun fromTrustState(value: TrustState) = value.name

    @ColumnTypeConverter
    fun toReceivers(value: String): Map<UserId, Long> = Json.decodeFromString(value)

    @ColumnTypeConverter
    fun fromReceivers(value: Map<UserId, Long>): String = Json.encodeToString(value)
}

@Entity(primaryKeys = ["roomId", "eventId"])
data class SummaryHeadEntity(
    val roomId: String,
    val eventId: String,
)

@Dao
interface SummaryHeadDao {
    @Insert(onConflict = OnConflictStrategy.REPLACE)
    suspend fun insert(entity: SummaryHeadEntity)

    @Query("DELETE FROM SummaryHeadEntity WHERE roomId = :roomId")
    suspend fun clear(roomId: String)

    @Query("SELECT eventId FROM SummaryHeadEntity WHERE roomId = :roomId")
    suspend fun getHeads(roomId: String): List<String>

    @Query("SELECT eventId FROM SummaryHeadEntity WHERE roomId = :roomId")
    fun getHeadsFlow(roomId: String): Flow<List<String>>

    @Query("DELETE FROM SummaryHeadEntity WHERE roomId = :roomId AND eventId = :head")
    suspend fun removeHead(roomId: String, head: String)
}
